'use client';

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/* ================================================================
   ConfirmDialog — 确认弹窗（替代 window.confirm）
   ================================================================ */

interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string | null;
  loadingLabel?: string;
  /** 危险操作时按钮变红 */
  variant?: 'default' | 'danger';
  loading?: boolean;
  error?: string;
}

function getDialogFocusableElements(dialog: HTMLElement): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((element) => !element.hasAttribute('aria-hidden'));
}

export function ConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loadingLabel,
  variant = 'default',
  loading = false,
  error,
}: ConfirmDialogProps) {
  const titleId = React.useId();
  const messageId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const cancelButtonRef = React.useRef<HTMLButtonElement>(null);
  const confirmButtonRef = React.useRef<HTMLButtonElement>(null);
  const onCancelRef = React.useRef(onCancel);
  const loadingRef = React.useRef(loading);
  onCancelRef.current = onCancel;
  loadingRef.current = loading;

  React.useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusFrame = window.requestAnimationFrame(() => {
      const initialFocus = cancelButtonRef.current ?? confirmButtonRef.current ?? dialogRef.current;
      initialFocus?.focus({ preventScroll: true });
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (!loadingRef.current) {
          onCancelRef.current();
        }
        return;
      }

      if (event.key !== 'Tab') return;
      const focusableElements = getDialogFocusableElements(dialog);
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }
      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;
      if (!dialog.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (event.shiftKey && (activeElement === first || activeElement === dialog)) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('keydown', handleKeyDown, true);
      if (previouslyFocused?.isConnected && previouslyFocused !== document.body) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  const isDanger = variant === 'danger';

  return (
    isOpen && (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center">
        <div
          aria-hidden="true"
          className="absolute inset-0 cursor-default bg-black/30 backdrop-blur-sm"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (!loading) onCancel();
          }}
        />

        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-busy={loading}
          aria-labelledby={titleId}
          aria-describedby={message ? messageId : undefined}
          tabIndex={-1}
          data-studio-surface
          className="relative mx-4 max-h-[calc(100dvh-32px)] w-full max-w-[360px] overflow-y-auto rounded-2xl border border-hr-border bg-white p-6 shadow-[var(--studio-shadow-overlay)]"
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <div className={cn(
              'flex h-10 w-10 items-center justify-center rounded-full',
              isDanger ? 'bg-red-50' : 'bg-accent-soft',
            )}>
              <AlertTriangle className={cn(
                'h-5 w-5',
                isDanger ? 'text-red-500' : 'text-accent-core',
              )} />
            </div>

            <div>
              <h3 id={titleId} className="text-lg font-semibold tracking-tight text-hr-text-primary">{title}</h3>
              {message && (
                <p id={messageId} className="mt-2 text-sm leading-relaxed text-hr-text-secondary">{message}</p>
              )}
            </div>

            {error && <p role="alert" className="text-xs leading-relaxed text-red-600">{error}</p>}
            <div className="mt-1 flex w-full gap-2">
              {cancelLabel ? (
                <Button
                  ref={cancelButtonRef}
                  type="button"
                  onClick={onCancel}
                  disabled={loading}
                  variant="secondary"
                  className="h-auto min-h-11 flex-1 whitespace-normal py-2"
                >
                  {cancelLabel}
                </Button>
              ) : null}
              <Button
                ref={confirmButtonRef}
                type="button"
                onClick={onConfirm}
                disabled={loading}
                variant={isDanger ? 'danger' : 'primary'}
                className="h-auto min-h-11 flex-1 whitespace-normal py-2"
              >
                {loading ? (loadingLabel ?? confirmLabel) : confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  );
}
